import type {
  ProductData,
  ProductMasterExportBundle,
} from './productMasterTypes';
import {
  buildProductMasterExportColumnOrder,
  EXPORT_DETAIL_HEADER_ADMIN_WEB_PROD,
  EXPORT_DETAIL_HEADER_ADMIN_WEB_STAGING,
  EXPORT_DETAIL_HEADER_ADMIN_WEB_WORKFLOW,
  EXPORT_DETAIL_HEADER_AWS_PROD,
  EXPORT_DETAIL_HEADER_AWS_STAGING,
  EXPORT_DETAIL_HEADER_AZURE_PROD,
  EXPORT_DETAIL_HEADER_AZURE_STAGING,
  EXPORT_DETAIL_HEADER_ENVIRONMENT,
  EXPORT_GITHUB_BACKEND_HEADER,
  EXPORT_GITHUB_FRONTEND_HEADER,
  EXPORT_RELATIONS_HEADER,
  GOVERNMENT_REGULATION_DEFS,
  JIIMA_CERTIFICATION_EXPIRY_EXPORT_HEADER,
  JIIMA_CERTIFICATION_LINE_DEFS,
  STANDARD_REGULATION_DEFS,
  STATIC_TABLE_HEADER,
  type ProductMasterOptionalStaticColumnKey,
} from './productMasterTableOptionalColumnModel';
import {
  EMPTY_PLATFORM_MAPPINGS,
  formatPlatformMappingsAliasesLine,
  formatPlatformMappingsK8sNamespacesLine,
  formatPlatformMappingsSonarLine,
  type PlatformMappings,
} from './platformMappings';
import { normalizeProductLifecycleValue } from './productLifecycle';
import { formatProductMasterRecordDate } from './productMasterRecordDates';
import {
  EXPORT_BACKEND_TECH_STACK_HEADER,
  EXPORT_FRONTEND_TECH_STACK_HEADER,
  techStackSummariesForMappings,
  type ProductMasterTechStackInventory,
  type ProductMasterTechStackSummaries,
} from './productMasterTechStackSummary';
import {
  formatSurfaceTypesDisplay,
  normalizeSurfaceTypes,
} from './productSurfaceTypes';

/**
 * Pure export row / CSV / JSON helpers shared by the Product Master UI and dfp-backend.
 * Browser-only fetch helpers live in the `plugin-dfp` package.
 */
export type {
  ProductData,
  ProductMasterExportBundle,
} from './productMasterTypes';

export function productMasterDetailsAsRecord(
  detail: unknown,
): Record<string, unknown> | undefined {
  if (typeof detail === 'object' && detail !== null && !Array.isArray(detail)) {
    return detail as Record<string, unknown>;
  }
  return undefined;
}

/** String form for bigint export fields (radix-10 via `BigInt.prototype.toString` avoids Sonar template-stringification warnings). */
export function bigintToExportString(value: bigint): string {
  return BigInt.prototype.toString.call(value, 10);
}

/** Max parallel detail fetches per export (limits burst load on browser + backend). */
export const PRODUCT_MASTER_EXPORT_CONCURRENCY = 8;

/**
 * Max product codes per POST /product-master/export (dfp-backend abuse limit).
 * Chunk larger lists so optional table columns can load the full filtered set.
 */
export const PRODUCT_MASTER_BULK_EXPORT_MAX_CODES = 500;

/**
 * Core list columns — titles must match `ProductMasterComponent` table headers.
 * (Excludes synthetic `ProductData` fields such as `catalogSearchHaystack`.)
 */
export const PRODUCT_MASTER_TABLE_LIST_HEADERS = {
  productCode: 'Product Code',
  category: 'Product Category',
  segment: 'Segment/Company',
  domain: 'Domain',
  division: 'Division/Department',
  lifecycle: 'Lifecycle Status',
  surfaceTypes: 'Surface Type',
} as const;

export type ProductMasterTableListColumnKey =
  keyof typeof PRODUCT_MASTER_TABLE_LIST_HEADERS;

/** CSV / JSON key when export fails for a product row. */
export const EXPORT_ERROR_HEADER = 'Export error';

/** Default CSV/JSON column order when no Dev/Biz roles appear in the export batch. */
export const PRODUCT_MASTER_DEFAULT_EXPORT_COLUMN_ORDER: readonly string[] =
  buildProductMasterExportColumnOrder([], []);

export interface ProductMasterExportRowOptions {
  /** Union of dev contact role labels across the export (same ordering as the table). */
  unionDevRoles?: readonly string[];
  /** Union of biz contact role labels across the export. */
  unionBizRoles?: readonly string[];
  /**
   * Derived tech-stack summaries keyed by product code. Present (including blank
   * strings) when the export fetched the inventory, including a failed fetch.
   */
  techStackByProductCode?: Readonly<
    Record<string, ProductMasterTechStackSummaries>
  >;
}

const BLANK_TECH_STACK_SUMMARY: ProductMasterTechStackSummaries = {
  frontend: '',
  backend: '',
};

/** Summaries for one export batch. A missing inventory yields blank strings. */
export function techStackExportByProductCode(
  bundles: readonly ProductMasterExportBundle[],
  inventory: ProductMasterTechStackInventory | null,
): Record<string, ProductMasterTechStackSummaries> {
  const out: Record<string, ProductMasterTechStackSummaries> = {};
  for (const bundle of bundles) {
    if (!inventory) {
      out[bundle.productCode] = { ...BLANK_TECH_STACK_SUMMARY };
      continue;
    }
    const detail = productMasterDetailsAsRecord(bundle.productMasterDetails);
    const mappings = detail
      ? platformMappingsFromDetail(detail)
      : { ...EMPTY_PLATFORM_MAPPINGS };
    out[bundle.productCode] = techStackSummariesForMappings(
      bundle.productCode,
      mappings.githubFrontend,
      mappings.githubBackend,
      inventory,
    );
  }
  return out;
}

/** Combined core contact columns (replaces separate owner / emergency / SRE detail fields). */
export const CORE_OWNER_CONTACT_HEADER = 'Core Owner Contact';
export const CORE_EMERGENCY_CONTACT_HEADER = 'Core Emergency Contact';
export const CORE_SRE_CONTACT_HEADER = 'Core SRE Contact';

const EXPORT_CELL_MISSING = '---';

/** Labels for core contact export lines (deduplicated for Sonar literal rules). */
const EXPORT_CORE_LABEL_CHANNEL = 'Channel';
const EXPORT_CORE_LABEL_MENTION = 'Mention';
const EXPORT_CORE_LABEL_MEMBERS = 'Members';

/** Detail keys never emitted as raw columns (computed, merged, or dropped from export). */
const SKIP_DETAIL_KEYS_FOR_EXPORT = new Set([
  'category',
  'domain',
  'segment',
  'division',
  'productCode',
  'lifecycle',
  'divisionSystemName',
  'description',
  'productDescription',
  'regulations',
  'jiimaCertification',
  'productTitle',
  'resources',
  'services',
  'providesApis',
  'consumesApis',
  'apis',
  'dependsOnProducts',
  'dependencyOfProducts',
  'surfaceTypes',
  'links',
  'techDocsUrl',
  'githubSourceUrl',
  'platformMappings',
  'productCodeMappings',
  'createdAt',
  'updatedAt',
]);

const OWNER_DETAIL_KEYS_ABSORBED = new Set([
  'ownerTitle',
  'ownerCatalogUrl',
  'ownerChannel',
  'ownerChannelUrl',
  'ownerChannelId',
  'ownerMention',
  'ownerMembers',
]);

const EMERGENCY_DETAIL_KEYS_ABSORBED = new Set([
  'emergencyTitle',
  'emergencyCatalogUrl',
  'emergencyChannel',
  'emergencyChannelUrl',
  'emergencyChannelId',
  'emergencyMention',
  'emergencyMembers',
]);

const SRE_DETAIL_KEYS_ABSORBED = new Set([
  'sreTitle',
  'sreCatalogUrl',
  'sreChannel',
  'sreChannelUrl',
  'sreChannelId',
  'sreMention',
  'sreMembers',
]);

function asNonEmptyExportString(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : undefined;
}

const MEMBER_EXPORT_EMAIL_DOMAIN = '@moneyforward.co.jp';

/** Catalog usernames are the local part. Export the full address, not the display name. */
function memberExportEmail(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.includes('@')) {
    return trimmed;
  }
  return `${trimmed}${MEMBER_EXPORT_EMAIL_DOMAIN}`;
}

function memberExportId(member: unknown): string | undefined {
  if (typeof member === 'string') {
    const text = asNonEmptyExportString(member);
    return text ? memberExportEmail(text) : undefined;
  }
  if (!member || typeof member !== 'object' || Array.isArray(member)) {
    return undefined;
  }
  const rec = member as { email?: unknown; name?: unknown };
  const email = asNonEmptyExportString(rec.email);
  if (email) {
    return memberExportEmail(email);
  }
  const name = asNonEmptyExportString(rec.name);
  return name ? memberExportEmail(name) : undefined;
}

function memberExportIds(members: unknown): string[] {
  const list = Array.isArray(members) ? members : [];
  const ids: string[] = [];
  for (const member of list) {
    const id = memberExportId(member);
    if (id) {
      ids.push(id);
    }
  }
  return ids;
}

/** Full emails for a contact member list. Shared with the API-shaped JSON export. */
export function exportContactMemberEmails(members: unknown): string[] {
  return memberExportIds(members);
}

function splitMentionList(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    const parts: string[] = [];
    for (const item of raw) {
      const text = asNonEmptyExportString(item);
      if (text) {
        parts.push(text);
      }
    }
    return parts;
  }
  const one = asNonEmptyExportString(raw);
  if (!one) {
    return [];
  }
  return one
    .split(',')
    .map(part => part.trim())
    .filter(part => part.length > 0);
}

function joinOrMissing(values: readonly string[]): string {
  return values.length > 0 ? values.join(', ') : EXPORT_CELL_MISSING;
}

export type ProductMasterExportContact = {
  channel: string | null;
  mention: string[];
  members: string[];
};

export type ProductMasterExportRoleContact = {
  members: string[];
  mention: string[];
};

function contactExportValue(
  channel: unknown,
  mention: unknown,
  members: unknown,
): ProductMasterExportContact {
  return {
    channel: asNonEmptyExportString(channel) ?? null,
    mention: splitMentionList(mention),
    members: memberExportIds(members),
  };
}

function contactToCsv(contact: ProductMasterExportContact): string {
  return [
    `${EXPORT_CORE_LABEL_CHANNEL}: ${contact.channel ?? EXPORT_CELL_MISSING}`,
    `${EXPORT_CORE_LABEL_MENTION}: ${joinOrMissing(contact.mention)}`,
    `${EXPORT_CORE_LABEL_MEMBERS}: ${joinOrMissing(contact.members)}`,
  ].join(' | ');
}

export function formatCoreOwnerContactExport(
  detail: Record<string, unknown>,
): string {
  return contactToCsv(
    contactExportValue(
      detail.ownerChannel,
      detail.ownerMention,
      detail.ownerMembers,
    ),
  );
}

export function formatCoreEmergencyContactExport(
  detail: Record<string, unknown>,
): string {
  return contactToCsv(
    contactExportValue(
      detail.emergencyChannel,
      detail.emergencyMention,
      detail.emergencyMembers,
    ),
  );
}

export function formatCoreSreContactExport(
  detail: Record<string, unknown>,
): string {
  return contactToCsv(
    contactExportValue(detail.sreChannel, detail.sreMention, detail.sreMembers),
  );
}

function findRoleContactRow(
  contacts: unknown,
  roleLabel: string,
): Record<string, unknown> | undefined {
  if (!Array.isArray(contacts)) {
    return undefined;
  }
  for (const row of contacts) {
    if (row && typeof row === 'object' && !Array.isArray(row)) {
      const r = (row as { role?: unknown }).role;
      if (typeof r === 'string' && r.trim() === roleLabel.trim()) {
        return row as Record<string, unknown>;
      }
    }
  }
  return undefined;
}

function memberIdsFromRoleContactRow(row: Record<string, unknown>): string[] {
  const fromList = memberExportIds(row.members);
  if (fromList.length > 0) {
    return fromList;
  }
  const single = memberExportId(row.member);
  return single ? [single] : [];
}

/**
 * One role cell as the DFP API `user` string: full emails joined with `", "`, or null.
 * Slack `@mentions` stay on {@link exportRoleMention}.
 */
export function exportRoleUserEmail(row: unknown): string | null {
  if (!row || typeof row !== 'object' || Array.isArray(row)) {
    return null;
  }
  const emails = memberIdsFromRoleContactRow(row as Record<string, unknown>);
  return emails.length > 0 ? emails.join(', ') : null;
}

/** Slack `@` tokens from a role cell, joined with `", "`, or null. */
export function exportRoleMention(row: unknown): string | null {
  if (!row || typeof row !== 'object' || Array.isArray(row)) {
    return null;
  }
  const mentions = mentionStringsFromRoleContactRow(
    row as Record<string, unknown>,
  );
  return mentions.length > 0 ? mentions.join(', ') : null;
}

function mentionStringsFromRoleContactRow(
  row: Record<string, unknown>,
): string[] {
  const mentionParts: string[] = [];
  const mentionsArr = row.mentions;
  if (Array.isArray(mentionsArr)) {
    for (const m of mentionsArr) {
      const s = asNonEmptyExportString(m);
      if (s) {
        mentionParts.push(s);
      }
    }
  }
  if (mentionParts.length === 0) {
    const one = asNonEmptyExportString(row.mention);
    if (one) {
      mentionParts.push(one);
    }
  }
  return mentionParts;
}

function roleContactExportValue(
  contacts: unknown,
  roleLabel: string,
): ProductMasterExportRoleContact {
  const row = findRoleContactRow(contacts, roleLabel);
  if (!row) {
    return { members: [], mention: [] };
  }
  return {
    members: memberIdsFromRoleContactRow(row),
    mention: mentionStringsFromRoleContactRow(row),
  };
}

function roleContactToCsv(contact: ProductMasterExportRoleContact): string {
  return `${EXPORT_CORE_LABEL_MEMBERS}: ${joinOrMissing(
    contact.members,
  )} | ${EXPORT_CORE_LABEL_MENTION}: ${joinOrMissing(contact.mention)}`;
}

/** Plain-text cell matching optional-column table rendering for Dev/Biz role columns. */
export function formatDevBizRoleContactForExport(
  contacts: unknown,
  roleLabel: string,
): string {
  return roleContactToCsv(roleContactExportValue(contacts, roleLabel));
}

function detailKeyAbsorbedIntoCombined(k: string): boolean {
  return (
    OWNER_DETAIL_KEYS_ABSORBED.has(k) ||
    EMERGENCY_DETAIL_KEYS_ABSORBED.has(k) ||
    SRE_DETAIL_KEYS_ABSORBED.has(k) ||
    k === 'devContacts' ||
    k === 'bizContacts' ||
    k === 'adminWebProdUrl' ||
    k === 'adminWebStagingUrl' ||
    k === 'awebWorkflowUrl'
  );
}

function pickProductDescriptionForExport(
  detail: Record<string, unknown>,
): string {
  const raw = detail.description ?? detail.productDescription;
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim();
  }
  return EXPORT_CELL_MISSING;
}

function regulationsRecord(
  detail: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const reg = detail.regulations;
  if (reg && typeof reg === 'object' && !Array.isArray(reg)) {
    return reg as Record<string, unknown>;
  }
  return undefined;
}

function regulationFlagMap(
  detail: Record<string, unknown>,
  defs: ReadonlyArray<{ key: string; label: string }>,
): Record<string, boolean> {
  const rec = regulationsRecord(detail);
  const out: Record<string, boolean> = {};
  for (const { key } of defs) {
    out[key] = rec?.[key] === true;
  }
  return out;
}

function regulationFlagMapToCsv(
  flags: Record<string, boolean>,
  defs: ReadonlyArray<{ key: string; label: string }>,
): string {
  return defs
    .map(({ key, label }) => `${label}: ${flags[key] === true ? 'Yes' : 'No'}`)
    .join(' | ');
}

export function formatRegulationsStandardExport(
  detail: Record<string, unknown>,
): string {
  return regulationFlagMapToCsv(
    regulationFlagMap(detail, STANDARD_REGULATION_DEFS),
    STANDARD_REGULATION_DEFS,
  );
}

export function formatRegulationsGovernmentExport(
  detail: Record<string, unknown>,
): string {
  return regulationFlagMapToCsv(
    regulationFlagMap(detail, GOVERNMENT_REGULATION_DEFS),
    GOVERNMENT_REGULATION_DEFS,
  );
}

export function formatJiimaSpecFieldExport(
  detail: Record<string, unknown>,
  specField: string,
): string {
  const j = detail.jiimaCertification;
  if (!j || typeof j !== 'object' || Array.isArray(j)) {
    return EXPORT_CELL_MISSING;
  }
  return (
    asNonEmptyExportString((j as Record<string, unknown>)[specField]) ??
    EXPORT_CELL_MISSING
  );
}

export type ProductMasterExportJiima = Record<string, string | null>;

function jiimaExportValue(
  detail: Record<string, unknown>,
): ProductMasterExportJiima {
  const out: ProductMasterExportJiima = {};
  for (const { specField } of JIIMA_CERTIFICATION_LINE_DEFS) {
    const raw = formatJiimaSpecFieldExport(detail, specField);
    out[specField] = raw === EXPORT_CELL_MISSING ? null : raw;
  }
  return out;
}

function jiimaToCsv(jiima: ProductMasterExportJiima): string {
  return JIIMA_CERTIFICATION_LINE_DEFS.map(({ specField, lineLabel }) => {
    const raw = jiima[specField];
    const text =
      typeof raw === 'string' && raw.trim() ? raw.trim() : EXPORT_CELL_MISSING;
    return `${lineLabel}: ${text}`;
  }).join(' | ');
}

/** One CSV cell: `Label: value` segments joined with ` | `. JSON uses {@link jiimaExportValue}. */
export function formatJiimaCertificationExpiryCombinedExport(
  detail: Record<string, unknown>,
): string {
  return jiimaToCsv(jiimaExportValue(detail));
}

export type ProductMasterExportDocumentation = {
  tech_docs: string | null;
};

function documentationExportValue(
  d: Record<string, unknown>,
  productCode: string,
): ProductMasterExportDocumentation {
  const techFromDetail = asNonEmptyExportString(d.techDocsUrl);
  const code = typeof productCode === 'string' ? productCode.trim() : '';
  const fallback = code
    ? `/docs/moneyforward/component/${encodeURIComponent(code)}`
    : null;
  return {
    tech_docs: techFromDetail ?? fallback,
  };
}

function orMissing(value: string | null): string {
  return value ?? EXPORT_CELL_MISSING;
}

function documentationToCsv(doc: ProductMasterExportDocumentation): string {
  return `TechDocs: ${orMissing(doc.tech_docs)}`;
}

/** One Documentation CSV cell: TechDocs. */
export function formatDocumentationCombinedExport(
  d: Record<string, unknown>,
  productCode: string,
): string {
  return documentationToCsv(documentationExportValue(d, productCode));
}

export type ProductMasterExportLink = {
  title: string | null;
  url: string | null;
};

function linksExportValue(
  detail: Record<string, unknown>,
): ProductMasterExportLink[] {
  const arr = detail.links;
  if (!Array.isArray(arr)) {
    return [];
  }
  const links: ProductMasterExportLink[] = [];
  for (const item of arr) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      continue;
    }
    const url = asNonEmptyExportString((item as { url?: unknown }).url) ?? null;
    const title =
      asNonEmptyExportString((item as { title?: unknown }).title) ?? null;
    if (url || title) {
      links.push({ title, url });
    }
  }
  return links;
}

function linksToCsv(links: readonly ProductMasterExportLink[]): string {
  if (links.length === 0) {
    return EXPORT_CELL_MISSING;
  }
  return links
    .map(link => {
      if (link.url && link.title) {
        return `${link.title}: ${link.url}`;
      }
      return link.url ?? link.title ?? EXPORT_CELL_MISSING;
    })
    .join(' | ');
}

/** One Links CSV cell: `metadata.links` entries joined with ` | `. */
export function formatMetadataLinksExport(
  detail: Record<string, unknown>,
): string {
  return linksToCsv(linksExportValue(detail));
}

export type ProductMasterExportNamedRef = {
  name: string | null;
  title: string | null;
};

function namedRefsExport(rows: unknown): ProductMasterExportNamedRef[] {
  if (!Array.isArray(rows)) {
    return [];
  }
  const refs: ProductMasterExportNamedRef[] = [];
  for (const item of rows) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      continue;
    }
    const name =
      asNonEmptyExportString((item as { name?: unknown }).name) ?? null;
    const title =
      asNonEmptyExportString((item as { title?: unknown }).title) ?? null;
    if (name || title) {
      refs.push({ name, title });
    }
  }
  return refs;
}

function namedRefsToCsv(refs: readonly ProductMasterExportNamedRef[]): string {
  if (refs.length === 0) {
    return EXPORT_CELL_MISSING;
  }
  return refs
    .map(ref => {
      if (ref.name && ref.title && ref.title !== ref.name) {
        return `${ref.title} (${ref.name})`;
      }
      return ref.name ?? ref.title ?? EXPORT_CELL_MISSING;
    })
    .join(' | ');
}

export type ProductMasterExportRelations = {
  dependencies: {
    depends_on: ProductMasterExportNamedRef[];
    dependency_of: ProductMasterExportNamedRef[];
  };
  apis: {
    consumes: ProductMasterExportNamedRef[];
    provides: ProductMasterExportNamedRef[];
  };
  resources: {
    depends_on: ProductMasterExportNamedRef[];
  };
  k8s_services: number;
};

function relationsExportValue(
  detail: Record<string, unknown>,
): ProductMasterExportRelations {
  return {
    dependencies: {
      depends_on: namedRefsExport(detail.dependsOnProducts),
      dependency_of: namedRefsExport(detail.dependencyOfProducts),
    },
    apis: {
      consumes: namedRefsExport(detail.consumesApis),
      provides: namedRefsExport(detail.providesApis),
    },
    resources: {
      depends_on: namedRefsExport(detail.resources),
    },
    k8s_services: Array.isArray(detail.services) ? detail.services.length : 0,
  };
}

function relationsToCsv(relations: ProductMasterExportRelations): string {
  return [
    `Dependencies: Depends on: ${namedRefsToCsv(
      relations.dependencies.depends_on,
    )}; Dependency Of: ${namedRefsToCsv(relations.dependencies.dependency_of)}`,
    `APIs: Consumes: ${namedRefsToCsv(
      relations.apis.consumes,
    )}; Provides: ${namedRefsToCsv(relations.apis.provides)}`,
    `Resources: Depends on: ${namedRefsToCsv(relations.resources.depends_on)}`,
    `K8s Services: ${relations.k8s_services}`,
  ].join(' | ');
}

/** One Relations CSV cell. JSON uses {@link relationsExportValue}. */
export function formatRelationsCombinedExport(
  detail: Record<string, unknown>,
): string {
  return relationsToCsv(relationsExportValue(detail));
}

/**
 * CSV / JSON keys for `productMasterDetails` (aligned with aggregated detail payload).
 * Optional static columns use {@link STATIC_TABLE_HEADER} first; remaining keys use this map,
 * then {@link humanizeCamelExportKey}. Keys omitted here are skipped in export or merged elsewhere (e.g. JIIMA).
 */
export const PRODUCT_MASTER_DETAIL_EXPORT_HEADERS: Record<string, string> = {
  productNameEn: 'Product Name (EN)',
  productNameJp: 'Product Name (JP)',
  environment: EXPORT_DETAIL_HEADER_ENVIRONMENT,
  awsProdAccount: EXPORT_DETAIL_HEADER_AWS_PROD,
  awsStagingAccount: EXPORT_DETAIL_HEADER_AWS_STAGING,
  azureProdSubscription: EXPORT_DETAIL_HEADER_AZURE_PROD,
  azureStagingSubscription: EXPORT_DETAIL_HEADER_AZURE_STAGING,
};

export function platformMappingsFromDetail(
  detail: Record<string, unknown>,
): PlatformMappings {
  const raw = detail.platformMappings;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    const stringList = (value: unknown): string[] =>
      Array.isArray(value)
        ? value.filter((x): x is string => typeof x === 'string')
        : [];
    return {
      productCodes: stringList(o.productCodes),
      sonarqube: stringList(o.sonarqube),
      k8sNamespaces: stringList(o.k8sNamespaces),
      githubFrontend: stringList(o.githubFrontend),
      githubBackend: stringList(o.githubBackend),
    };
  }
  return { ...EMPTY_PLATFORM_MAPPINGS };
}

export type ProductMasterExportPlatformMappings = {
  product_code_aliases: string[];
  sonarqube_project_keys: string[];
  k8s_namespaces: string[];
  github_frontend: string[];
  github_backend: string[];
};

function platformMappingsExportValue(
  detail: Record<string, unknown>,
): ProductMasterExportPlatformMappings {
  const mappings = platformMappingsFromDetail(detail);
  return {
    product_code_aliases: mappings.productCodes,
    sonarqube_project_keys: mappings.sonarqube,
    k8s_namespaces: mappings.k8sNamespaces,
    github_frontend: mappings.githubFrontend,
    github_backend: mappings.githubBackend,
  };
}

function platformMappingsToCsv(
  mappings: ProductMasterExportPlatformMappings,
): string {
  return [
    formatPlatformMappingsAliasesLine(mappings.product_code_aliases),
    formatPlatformMappingsSonarLine(mappings.sonarqube_project_keys),
    formatPlatformMappingsK8sNamespacesLine(mappings.k8s_namespaces),
  ].join(' | ');
}

function githubReposExportCell(values: readonly string[]): string {
  return values.filter(value => value.trim().length > 0).join(', ');
}

export function formatPlatformMappingsCombinedExport(
  detail: Record<string, unknown>,
): string {
  return platformMappingsToCsv(platformMappingsExportValue(detail));
}

export function stringifyCell(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return value.toString();
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'bigint') {
    return bigintToExportString(value);
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  if (typeof value === 'symbol') {
    return value.toString();
  }
  if (typeof value === 'function') {
    return '';
  }
  return '';
}

/**
 * JSON file rows keep structured values (contacts, accounts, relations).
 * CSV turns those objects into labeled text in {@link flattenExportBundleForCsv}.
 */
export function normalizeExportRowValuesForJsonFile(
  row: Record<string, unknown>,
): Record<string, unknown> {
  return { ...row };
}

/**
 * JSON file export uses snake_case keys (e.g. `exported_at`, `product_code`). CSV keeps
 * human-readable column titles unchanged.
 */
export function exportJsonKeyToSnakeCase(key: string): string {
  const trimmed = key.trim();
  if (!trimmed) {
    return '';
  }
  const decamelized = trimmed
    .replaceAll(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replaceAll(/([A-Z])([A-Z][a-z])/g, '$1_$2');
  return decamelized
    .replaceAll(/[^a-zA-Z0-9]+/g, '_')
    .replaceAll(/_+/g, '_')
    .replaceAll(/(?:^_|_$)/g, '')
    .toLowerCase();
}

export function mapJsonExportRecordKeysToSnakeCase(
  row: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) {
    out[exportJsonKeyToSnakeCase(k)] = v;
  }
  return out;
}

export function escapeCsvField(raw: string): string {
  const needsQuotes = /[",\r\n]/.test(raw);
  const doubled = raw.replaceAll('"', '""');
  return needsQuotes ? `"${doubled}"` : doubled;
}

export function humanizeCamelExportKey(key: string): string {
  const spaced = key
    .replaceAll(/([A-Z])/g, ' $1')
    .replaceAll('_', ' ')
    .trim();
  if (!spaced) {
    return key;
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function allocateHeader(row: Record<string, unknown>, base: string): string {
  if (!(base in row)) {
    return base;
  }
  let h = `Detail — ${base}`;
  let i = 2;
  while (h in row) {
    h = `Detail (${i}) — ${base}`;
    i += 1;
  }
  return h;
}

function assignTechStackExportFields(
  row: Record<string, unknown>,
  productCode: string,
  options: ProductMasterExportRowOptions | undefined,
): void {
  const map = options?.techStackByProductCode;
  if (!map) {
    return;
  }
  const hit = map[productCode] ?? BLANK_TECH_STACK_SUMMARY;
  assignExportField(row, EXPORT_FRONTEND_TECH_STACK_HEADER, hit.frontend);
  assignExportField(row, EXPORT_BACKEND_TECH_STACK_HEADER, hit.backend);
}

function assignExportField(
  row: Record<string, unknown>,
  headerBase: string,
  value: unknown,
): void {
  const header = allocateHeader(row, headerBase);
  row[header] = value;
}

function exportHeaderForDetailKey(k: string): string {
  if (k === 'description' || k === 'productDescription') {
    return STATIC_TABLE_HEADER.productDescription;
  }
  if (Object.hasOwn(STATIC_TABLE_HEADER, k)) {
    return STATIC_TABLE_HEADER[k as ProductMasterOptionalStaticColumnKey];
  }
  return PRODUCT_MASTER_DETAIL_EXPORT_HEADERS[k] ?? humanizeCamelExportKey(k);
}

/**
 * Reorders a flat export row so JSON keys follow the same sequence as the table (then any
 * extra keys alphabetically).
 */
export function reorderExportRowKeys(
  row: Record<string, unknown>,
  columnOrder: readonly string[] = PRODUCT_MASTER_DEFAULT_EXPORT_COLUMN_ORDER,
): Record<string, unknown> {
  const ordered: Record<string, unknown> = {};
  const seen = new Set<string>();
  for (const k of columnOrder) {
    if (Object.hasOwn(row, k)) {
      ordered[k] = row[k];
      seen.add(k);
    }
  }
  const rest = Object.keys(row)
    .filter(k => !seen.has(k))
    .sort((a, b) => a.localeCompare(b));
  for (const k of rest) {
    ordered[k] = row[k];
  }
  return ordered;
}

/**
 * One JSON object per product: list columns, combined core contacts, per-role Dev/Biz columns
 * (when `options.union*` are set), then remaining detail fields (`Detail —` prefix only when a
 * header collides with an earlier field).
 */
function productListExportValue(
  listRow: ProductData,
  key: ProductMasterTableListColumnKey,
): unknown {
  if (key === 'lifecycle') {
    return normalizeProductLifecycleValue(listRow.lifecycle ?? '');
  }
  if (key === 'surfaceTypes') {
    return normalizeSurfaceTypes(listRow.surfaceTypes);
  }
  return listRow[key];
}

export function buildExportJsonRow(
  listRow: ProductData,
  bundle: ProductMasterExportBundle,
  options?: ProductMasterExportRowOptions,
): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  const unionDev = options?.unionDevRoles ?? [];
  const unionBiz = options?.unionBizRoles ?? [];

  (
    Object.keys(
      PRODUCT_MASTER_TABLE_LIST_HEADERS,
    ) as ProductMasterTableListColumnKey[]
  ).forEach(key => {
    assignExportField(
      row,
      PRODUCT_MASTER_TABLE_LIST_HEADERS[key],
      productListExportValue(listRow, key),
    );
  });

  assignTechStackExportFields(row, bundle.productCode, options);

  if (bundle.error) {
    assignExportField(row, EXPORT_ERROR_HEADER, bundle.error);
    return row;
  }

  const d = productMasterDetailsAsRecord(bundle.productMasterDetails);
  if (d) {
    assignExportField(
      row,
      STATIC_TABLE_HEADER.productDescription,
      pickProductDescriptionForExport(d),
    );
    assignExportField(
      row,
      STATIC_TABLE_HEADER.regulationsStandard,
      regulationFlagMap(d, STANDARD_REGULATION_DEFS),
    );
    assignExportField(
      row,
      STATIC_TABLE_HEADER.regulationsGovernment,
      regulationFlagMap(d, GOVERNMENT_REGULATION_DEFS),
    );
    assignExportField(
      row,
      JIIMA_CERTIFICATION_EXPIRY_EXPORT_HEADER,
      jiimaExportValue(d),
    );

    assignExportField(
      row,
      EXPORT_DETAIL_HEADER_ADMIN_WEB_PROD,
      asNonEmptyExportString(d.adminWebProdUrl) ?? EXPORT_CELL_MISSING,
    );
    assignExportField(
      row,
      EXPORT_DETAIL_HEADER_ADMIN_WEB_STAGING,
      asNonEmptyExportString(d.adminWebStagingUrl) ?? EXPORT_CELL_MISSING,
    );
    assignExportField(
      row,
      EXPORT_DETAIL_HEADER_ADMIN_WEB_WORKFLOW,
      asNonEmptyExportString(d.awebWorkflowUrl) ?? EXPORT_CELL_MISSING,
    );

    assignExportField(
      row,
      STATIC_TABLE_HEADER.documentation,
      documentationExportValue(d, listRow.productCode),
    );

    assignExportField(
      row,
      STATIC_TABLE_HEADER.metadataLinks,
      linksExportValue(d),
    );

    assignExportField(
      row,
      STATIC_TABLE_HEADER.platformMappings,
      platformMappingsExportValue(d),
    );
    const githubMappings = platformMappingsFromDetail(d);
    assignExportField(
      row,
      EXPORT_GITHUB_FRONTEND_HEADER,
      githubReposExportCell(githubMappings.githubFrontend),
    );
    assignExportField(
      row,
      EXPORT_GITHUB_BACKEND_HEADER,
      githubReposExportCell(githubMappings.githubBackend),
    );

    assignExportField(row, EXPORT_RELATIONS_HEADER, relationsExportValue(d));

    assignExportField(
      row,
      STATIC_TABLE_HEADER.createdAt,
      formatProductMasterRecordDate(d.createdAt, 'created') ??
        EXPORT_CELL_MISSING,
    );
    assignExportField(
      row,
      STATIC_TABLE_HEADER.updatedAt,
      formatProductMasterRecordDate(d.updatedAt, 'updated') ??
        EXPORT_CELL_MISSING,
    );

    assignExportField(
      row,
      CORE_OWNER_CONTACT_HEADER,
      contactExportValue(d.ownerChannel, d.ownerMention, d.ownerMembers),
    );
    assignExportField(
      row,
      CORE_EMERGENCY_CONTACT_HEADER,
      contactExportValue(
        d.emergencyChannel,
        d.emergencyMention,
        d.emergencyMembers,
      ),
    );
    assignExportField(
      row,
      CORE_SRE_CONTACT_HEADER,
      contactExportValue(d.sreChannel, d.sreMention, d.sreMembers),
    );

    for (const role of unionDev) {
      assignExportField(
        row,
        `Dev — ${role}`,
        roleContactExportValue(d.devContacts, role),
      );
    }
    for (const role of unionBiz) {
      assignExportField(
        row,
        `Biz — ${role}`,
        roleContactExportValue(d.bizContacts, role),
      );
    }

    for (const [k, v] of Object.entries(d)) {
      if (k.startsWith('_')) {
        continue;
      }
      if (
        SKIP_DETAIL_KEYS_FOR_EXPORT.has(k) ||
        detailKeyAbsorbedIntoCombined(k)
      ) {
        continue;
      }
      const base = exportHeaderForDetailKey(k);
      assignExportField(row, base, v);
    }
  }

  return row;
}

function isExportRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function csvCellForExportHeader(header: string, value: unknown): string {
  if (
    header === PRODUCT_MASTER_TABLE_LIST_HEADERS.surfaceTypes &&
    Array.isArray(value)
  ) {
    return formatSurfaceTypesDisplay(value);
  }
  if (
    (header === CORE_OWNER_CONTACT_HEADER ||
      header === CORE_EMERGENCY_CONTACT_HEADER ||
      header === CORE_SRE_CONTACT_HEADER) &&
    isExportRecord(value)
  ) {
    return contactToCsv(value as ProductMasterExportContact);
  }
  if (
    (header.startsWith('Dev — ') || header.startsWith('Biz — ')) &&
    isExportRecord(value)
  ) {
    return roleContactToCsv(value as ProductMasterExportRoleContact);
  }
  if (
    header === STATIC_TABLE_HEADER.regulationsStandard &&
    isExportRecord(value)
  ) {
    return regulationFlagMapToCsv(
      value as Record<string, boolean>,
      STANDARD_REGULATION_DEFS,
    );
  }
  if (
    header === STATIC_TABLE_HEADER.regulationsGovernment &&
    isExportRecord(value)
  ) {
    return regulationFlagMapToCsv(
      value as Record<string, boolean>,
      GOVERNMENT_REGULATION_DEFS,
    );
  }
  if (
    header === JIIMA_CERTIFICATION_EXPIRY_EXPORT_HEADER &&
    isExportRecord(value)
  ) {
    return jiimaToCsv(value as ProductMasterExportJiima);
  }
  if (header === STATIC_TABLE_HEADER.documentation && isExportRecord(value)) {
    return documentationToCsv(value as ProductMasterExportDocumentation);
  }
  if (header === STATIC_TABLE_HEADER.metadataLinks && Array.isArray(value)) {
    return linksToCsv(value as ProductMasterExportLink[]);
  }
  if (
    header === STATIC_TABLE_HEADER.platformMappings &&
    isExportRecord(value)
  ) {
    return platformMappingsToCsv(value as ProductMasterExportPlatformMappings);
  }
  if (header === EXPORT_RELATIONS_HEADER && isExportRecord(value)) {
    return relationsToCsv(value as ProductMasterExportRelations);
  }
  return stringifyCell(value);
}

export function flattenExportBundleForCsv(
  listRow: ProductData,
  bundle: ProductMasterExportBundle,
  options?: ProductMasterExportRowOptions,
): Record<string, string> {
  const wide = buildExportJsonRow(listRow, bundle, options);
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(wide)) {
    out[k] = csvCellForExportHeader(k, v);
  }
  return out;
}

export function formatExportError(e: unknown): string {
  if (e instanceof Error) {
    return e.message;
  }
  if (typeof e === 'object' && e !== null) {
    return JSON.stringify(e);
  }
  if (typeof e === 'string') {
    return e;
  }
  if (typeof e === 'number') {
    return e.toString();
  }
  if (typeof e === 'boolean') {
    return e ? 'true' : 'false';
  }
  if (typeof e === 'bigint') {
    return bigintToExportString(e);
  }
  if (typeof e === 'symbol') {
    return e.toString();
  }
  return '';
}

/** Case-insensitive product-code order, matching the Product Master list column. */
export function compareProductCodes(left: string, right: string): number {
  const primary = left.localeCompare(right, undefined, { sensitivity: 'base' });
  if (primary !== 0) {
    return primary;
  }
  if (left === right) {
    return 0;
  }
  return left < right ? -1 : 1;
}

/** Align list rows with bundles returned in the same order as `productCodes`. */
export function zipProductRowsWithBundles(
  filteredProducts: ProductData[],
  bundles: ProductMasterExportBundle[],
): Array<{ listRow: ProductData; bundle: ProductMasterExportBundle }> {
  return filteredProducts.map((listRow, i) => {
    const bundle = bundles[i];
    if (bundle === undefined) {
      return {
        listRow,
        bundle: {
          productCode: listRow.productCode,
          error: 'Missing bundle for row',
        },
      };
    }
    return { listRow, bundle };
  });
}

/**
 * Run async work on items with at most `concurrency` in flight at once (total work still `items.length`).
 */
export async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  if (items.length === 0) {
    return [];
  }
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  async function worker(): Promise<void> {
    for (;;) {
      const i = nextIndex;
      nextIndex += 1;
      if (i >= items.length) {
        return;
      }
      const item = items[i];
      if (item === undefined) {
        return;
      }
      results[i] = await mapper(item, i);
    }
  }

  const pool = Math.min(Math.max(1, concurrency), items.length);
  await Promise.all(Array.from({ length: pool }, () => worker()));
  return results;
}

export function buildCsvFromFlatRows(
  flatRows: Record<string, string>[],
  columnOrder: readonly string[] = PRODUCT_MASTER_DEFAULT_EXPORT_COLUMN_ORDER,
): string {
  const keySet = new Set<string>();
  flatRows.forEach(r => {
    Object.keys(r).forEach(k => keySet.add(k));
  });
  const prioritySet = new Set(columnOrder);
  const priority = columnOrder.filter(k => keySet.has(k));
  const rest = Array.from(keySet)
    .filter(k => !prioritySet.has(k))
    .sort((a, b) => a.localeCompare(b));
  const headers = [...priority, ...rest];
  const lines = [
    headers.map(escapeCsvField).join(','),
    ...flatRows.map(row =>
      headers.map(h => escapeCsvField(row[h] ?? '')).join(','),
    ),
  ];
  return lines.join('\r\n');
}
